/**
 * Pure helpers for the report editor: a field catalogue over the reporting
 * metadata, join-aware compatibility of fields with the current selection,
 * the join paths a selection uses, and display suggestions.
 * No React, no network.
 */
import { EMPTY_FORM, cubeOf, formFromConfig, displayToForm, reachableFrom } from '../../utils/cubeQuery';

/** "sales_transactions" -> "Sales Transactions". */
export const humanize = (name) => String(name || '')
  .replace(/^metric_/, '')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (c) => c.toUpperCase());

export const isMetricCube = (cube) => cube?.meta?.kind === 'metric';

/** Readable dataset title; Cube titles default to the raw name. */
export const cubeTitle = (cube) => {
  if (!cube) return '';
  // Cube defaults the title to the snake_case name; those read better humanised.
  const title = cube.title || cube.name;
  return /^[a-z0-9_]+$/.test(title) ? humanize(title) : title;
};

export const memberLabel = (m) => m?.shortTitle || m?.title || String(m?.name || '').split('.').pop();

/**
 * Normalised catalogue: visible cubes with visible members, each field tagged
 * with its kind ('measure' | 'dimension' | 'time') and its cube.
 * Returns { cubes, fields: Map<name, field>, byCube: Map<cube, cube> }.
 */
export function buildCatalogue(rawCubes = []) {
  const fields = new Map();
  const byCube = new Map();
  const cubes = rawCubes
    .filter((c) => c.isVisible !== false)
    .map((c) => {
      const measures = (c.measures || []).filter((m) => m.isVisible !== false).map((m) => ({
        name: m.name, label: memberLabel(m), kind: 'measure', cube: c.name, type: m.type, aggType: m.aggType, format: m.format || null, description: m.description,
      }));
      const dimensions = (c.dimensions || []).filter((d) => d.isVisible !== false).map((d) => ({
        name: d.name, label: memberLabel(d), kind: d.type === 'time' ? 'time' : 'dimension', cube: c.name, type: d.type, description: d.description,
      }));
      const cube = {
        name: c.name,
        title: cubeTitle(c),
        isMetric: isMetricCube(c),
        joins: c.joins || [],
        meta: c.meta || {},
        measures,
        dimensions,
      };
      for (const f of [...measures, ...dimensions]) fields.set(f.name, f);
      byCube.set(c.name, cube);
      return cube;
    })
    .sort((a, b) => Number(a.isMetric) - Number(b.isMetric) || a.title.localeCompare(b.title));
  return { cubes, fields, byCube };
}

/** Reach set (cube -> cubes reachable through declared joins, itself included). */
export function reachMap(cubes = []) {
  const map = new Map();
  for (const c of cubes) map.set(c.name, reachableFrom(cubes, c.name));
  return map;
}

/**
 * The cubes a form draws from, split by role. Filters on measures count as
 * measures (they become HAVING clauses), filters on dimensions as dimensions.
 */
export function selectionCubes(form, fields) {
  const measureCubes = new Set((form.measures || []).map(cubeOf).filter(Boolean));
  const dimensionCubes = new Set([...(form.dimensions || []), form.timeDimension].filter(Boolean).map(cubeOf));
  for (const f of form.filters || []) {
    if (!f.member) continue;
    const kind = fields?.get(f.member)?.kind;
    (kind === 'measure' ? measureCubes : dimensionCubes).add(cubeOf(f.member));
  }
  return { measureCubes, dimensionCubes };
}

/**
 * Whether measures from `measureCubes` can be combined with dimensions from
 * `dimensionCubes`. The engine needs one cube that reaches every cube in the
 * query through declared joins; on top of that, every measure's cube must
 * reach every dimension's cube so values are not grouped by unrelated rows.
 */
export function canCombine(reach, measureCubes, dimensionCubes) {
  const dims = [...dimensionCubes];
  const all = [...new Set([...measureCubes, ...dims])];
  if (all.length <= 1) return true;
  if (![...measureCubes].every((m) => dims.every((d) => reach.get(m)?.has(d)))) return false;
  return [...reach.values()].some((set) => all.every((c) => set.has(c)));
}

/**
 * Whether `field` can be added to the current selection. Fields already in
 * use are always available (so they can be removed or moved).
 * Returns { ok, reason, conflicts: [cube names] }.
 */
export function fieldStatus(field, form, catalogue, reach) {
  if (!field) return { ok: false, reason: 'Unknown field', conflicts: [] };
  const inUse = (form.measures || []).includes(field.name)
    || (form.dimensions || []).includes(field.name)
    || form.timeDimension === field.name;
  if (inUse) return { ok: true, reason: null, conflicts: [] };

  const { measureCubes, dimensionCubes } = selectionCubes(form, catalogue.fields);
  const m = new Set(measureCubes);
  const d = new Set(dimensionCubes);
  (field.kind === 'measure' ? m : d).add(field.cube);
  if (canCombine(reach, m, d)) return { ok: true, reason: null, conflicts: [] };

  const title = (name) => catalogue.byCube.get(name)?.title || name;
  let conflicts;
  if (field.kind === 'measure') {
    conflicts = [...dimensionCubes].filter((c) => !reach.get(field.cube)?.has(c));
  } else {
    conflicts = [...measureCubes].filter((c) => !reach.get(c)?.has(field.cube));
  }
  if (conflicts.length === 0) conflicts = [...new Set([...measureCubes, ...dimensionCubes])].filter((c) => c !== field.cube);
  return {
    ok: false,
    reason: `No relationship between ${title(field.cube)} and ${conflicts.map(title).join(', ')}`,
    conflicts,
  };
}

/** Shortest join path from one cube to another as edges [{ from, to, on }], or null. */
export function joinPath(cubes, from, to) {
  if (from === to) return [];
  const byName = new Map(cubes.map((c) => [c.name, c]));
  const prev = new Map([[from, null]]);
  const queue = [from];
  while (queue.length) {
    const name = queue.shift();
    for (const j of byName.get(name)?.joins || []) {
      if (prev.has(j.to)) continue;
      prev.set(j.to, { from: name, to: j.to, on: j.on });
      if (j.to === to) {
        const edges = [];
        for (let e = prev.get(to); e; e = prev.get(e.from)) edges.unshift(e);
        return edges;
      }
      queue.push(j.to);
    }
  }
  return null;
}

/**
 * The joins a valid selection relies on: from the cube that reaches every
 * other cube in the query (a measure's cube when possible) to each of them.
 * Deduplicated edges [{ from, to, on }].
 */
export function joinsInUse(form, catalogue, reach) {
  const { measureCubes, dimensionCubes } = selectionCubes(form, catalogue.fields);
  const all = [...new Set([...measureCubes, ...dimensionCubes])];
  if (all.length <= 1) return [];
  const reachesAll = (name) => all.every((c) => reach.get(name)?.has(c));
  const root = [...measureCubes].find(reachesAll) || [...reach.keys()].find(reachesAll);
  if (!root) return [];
  const seen = new Map();
  for (const c of all) {
    for (const e of joinPath(catalogue.cubes, root, c) || []) seen.set(`${e.from}>${e.to}`, e);
  }
  return [...seen.values()];
}

/** Cubes the selection touches. */
export function cubesInUse(form, fields) {
  const { measureCubes, dimensionCubes } = selectionCubes(form, fields);
  return new Set([...measureCubes, ...dimensionCubes]);
}

/** Readable title from the selection, e.g. "Hours Sum by Person and Month". */
export function suggestTitle(form, catalogue) {
  const label = (name) => catalogue.fields.get(name)?.label || String(name).split('.').pop();
  const measures = (form.measures || []).map(label);
  const groups = (form.dimensions || []).map(label);
  if (form.timeDimension && form.granularity) groups.push(form.granularity.replace(/^\w/, (c) => c.toUpperCase()));
  const list = (items) => (items.length <= 2 ? items.join(' and ') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);
  if (measures.length === 0 && groups.length === 0) return '';
  if (measures.length === 0) return list(groups);
  return groups.length ? `${list(measures)} by ${list(groups)}` : list(measures);
}

/** The display type that suits the selection best. */
export function recommendDisplay(form) {
  const measures = form.measures?.length || 0;
  const dims = form.dimensions?.length || 0;
  const timeBreakdown = !!(form.timeDimension && form.granularity);
  if (measures === 1 && dims === 0 && !timeBreakdown) return 'stat';
  if (timeBreakdown && dims === 0 && measures >= 1) return 'line';
  if (measures >= 1 && dims + (timeBreakdown ? 1 : 0) >= 2) return 'pivot';
  if (measures >= 1 && dims === 1) return 'bar';
  return 'table';
}

/**
 * Editor state from a saved report or an older widget config. Filter values
 * are kept as arrays so values containing commas survive.
 */
export function formFromReport(source, { defaultVisibility = 'company' } = {}) {
  if (!source) return { ...EMPTY_FORM, ...displayToForm({}, defaultVisibility) };
  const isReport = !!source.cubeQuery && source.display !== undefined;
  const config = isReport
    ? { title: source.name, displayType: source.display?.displayType, cubeQuery: source.cubeQuery, refreshMinutes: source.refreshMinutes }
    : source;
  const form = formFromConfig(config);
  form.filters = (config.cubeQuery?.filters || []).map((f) => ({
    member: f.member || f.dimension || '',
    operator: f.operator || 'equals',
    values: Array.isArray(f.values) ? f.values.map(String) : [],
  }));
  return {
    ...form,
    description: source.description || '',
    ...(isReport ? displayToForm(source.display || {}, source.visibility) : displayToForm({}, defaultVisibility)),
  };
}

/** Pivot layout default: the last group goes across the top, the rest down the side. */
export function defaultPivot(form) {
  const groups = [...(form.dimensions || []), ...(form.timeDimension && form.granularity ? [form.timeDimension] : [])];
  if (groups.length < 2) return { pivotRows: groups, pivotColumns: [] };
  return { pivotRows: groups.slice(0, -1), pivotColumns: groups.slice(-1) };
}

/**
 * Keep the pivot layout in step with the groups: drop fields no longer
 * grouped, put new ones down the side, and lay out from scratch when empty.
 */
export function normalizePivot(form) {
  if (form.displayType !== 'pivot') return form;
  const groups = [...(form.dimensions || []), ...(form.timeDimension && form.granularity ? [form.timeDimension] : [])];
  const rows = (form.pivotRows || []).filter((g) => groups.includes(g));
  const columns = (form.pivotColumns || []).filter((g) => groups.includes(g));
  if (rows.length === 0 && columns.length === 0) return { ...form, ...defaultPivot(form) };
  const unplaced = groups.filter((g) => !rows.includes(g) && !columns.includes(g));
  const values = [...(form.measures || []), ...(form.calculations || []).map((c) => `calc:${c.id}`)];
  const pivotMeasure = values.includes(form.pivotMeasure) ? form.pivotMeasure : '';
  return { ...form, pivotRows: [...rows, ...unplaced], pivotColumns: columns, pivotMeasure };
}

/** Members the form references that the catalogue no longer has. */
export function missingMembers(form, fields) {
  const names = [
    ...(form.measures || []), ...(form.dimensions || []), form.timeDimension,
    ...(form.filters || []).map((f) => f.member),
  ].filter(Boolean);
  return [...new Set(names.filter((n) => !fields.has(n)))];
}

/** Remove a member everywhere it is referenced in the form (order, pivot). */
export function withoutMember(form, name) {
  const next = {
    ...form,
    measures: (form.measures || []).filter((m) => m !== name),
    dimensions: (form.dimensions || []).filter((d) => d !== name),
    pivotRows: (form.pivotRows || []).filter((d) => d !== name),
    pivotColumns: (form.pivotColumns || []).filter((d) => d !== name),
  };
  if (form.timeDimension === name) {
    next.timeDimension = '';
    next.granularity = '';
    next.dateRange = '';
    next.compare = false;
  }
  if (form.pivotMeasure === name) next.pivotMeasure = '';
  if (form.orderMember === name || String(form.orderMember || '').startsWith(`${name}.`)) next.orderMember = '';
  // Settings that depend on the field go with it: Top N on it, calculations of it, colour rules on it.
  if (form.topN?.dimension === name) next.topN = null;
  else if (form.topN?.by === name) next.topN = { ...form.topN, by: null };
  const gone = new Set((form.calculations || []).filter((c) => c.measure === name).map((c) => `calc:${c.id}`));
  next.calculations = (form.calculations || []).filter((c) => c.measure !== name);
  next.colorRules = (form.colorRules || []).filter((r) => r.column !== name && r.column !== `compare:${name}` && !gone.has(r.column));
  if (gone.has(form.pivotMeasure)) next.pivotMeasure = '';
  return next;
}

/** Move an item within a list. */
export function moveItem(list, from, to) {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
